<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3RemoveGroupModal from "@/components/itemV3/parameter/itemV3RemoveGroupModal.vue";
import {computed, inject, reactive, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(false)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "grooves"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "grooves.*.groove_type "

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

// Tracks the box's own open/closed state (bound via v-model:open below) instead of a
// hardcoded "false", so it survives re-renders triggered by a patch - see
// itemV3ParameterBox.vue's onUpdated hook, which re-asserts this value on every update.
const open = ref(false)

const currentGroovesText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const grooves = computed(() => {
  const grooves: any = {}

  if (session.value && session.value.items && session.value.items[itemIdx.value]) {
    const firstItemVariants = session.value.items[itemIdx.value].variants || {};


    for (const key in firstItemVariants) {
      const variant = firstItemVariants[key];
      if (!variant?.idx) continue;

      let checkVariantId = variant.idx
      const parts = variant.idx.split('.');

      if (parts[0] != "grooves" || parts.length != 3) continue

      const grooveIdx = parseInt(parts[1].toString())
      const grooveIdxStr = "IDX" + grooveIdx

      if (typeof grooves[grooveIdxStr] == "undefined") {
        grooves[grooveIdxStr] = {
          "groove_idx": grooveIdx,
          "groove_type": "",
          "groove_width": 0,
          "groove_ampltute": 0,
          "object": "",
          "object_arrangement": "",
          "key": {}
        }
      }

      grooves[grooveIdxStr][parts[2]] = variant["text"]
      grooves[grooveIdxStr]["key"][parts[2]] = variant["key"] || ""

    }
  }

  //if (configureAll.value) {

  //}

  return grooves
})

/**
 * image
 */

const hoverGrooveTypeIdx = ref(-1)
const imageUrl = computed(() => {
  if (activeGroove.value.groove_type && activeGroove.value.groove_type.length > 0) {
    return parametersVariations.imageUrl(activeGroove.value.groove_type[0].values, hoverGrooveTypeIdx.value)
  } else {
    return parametersVariations.imageUrl([], -1)
  }
})


const hasGrooves = computed(() => Object.keys(grooves.value).length > 0)

/**
 * groove details
 */

const activeGrooveIdx = ref(-1)

const activeGroove = ref({
  "groove_type": {},
  "amplitude": {},
  "width": {},
  "object": {},
  "object_arrangement": {}
} as any)

const getActiveGrooveData = (grooveIdy: number) => {
  if (grooveIdy >= 0) {
    activeGroove.value.groove_type = {}
    activeGroove.value.width = {}
    activeGroove.value.object = {}
    activeGroove.value.object_arrangement = {}
    activeGroove.value.amplitude = {}
    // Mirrors itemV2GrooveConfigurator.vue's "activeGroove.grooveTypeVariant": the raw
    // "grooves.<n>.groove_type" variant, whose values are grouped by conf_groups (see
    // groupedGrooveTypeValues below, transferred from itemV2GrooveType.vue).
    activeGroove.value.groove_type = parametersVariations.getItemVariants(itemIdx.value, "grooves." + grooveIdy + ".groove_type")
    activeGroove.value.width = parametersVariations.getItemVariants(itemIdx.value, "grooves." + grooveIdy + ".width")
    activeGroove.value.object = parametersVariations.getItemVariants(itemIdx.value, "grooves." + grooveIdy + ".object")
    activeGroove.value.object_arrangement = parametersVariations.getItemVariants(itemIdx.value, "grooves." + grooveIdy + ".object_arrangement")
    activeGroove.value.amplitude = parametersVariations.getItemVariants(itemIdx.value, "grooves." + grooveIdy + ".amplitude")

  }
}

watch(activeGrooveIdx, () => {
  if (activeGrooveIdx.value >= 0) {
    getActiveGrooveData(activeGrooveIdx.value)
  }
  showObjectDropdown.value = false
})

watch(session, () => {
  if (activeGrooveIdx.value >= 0) {
    getActiveGrooveData(activeGrooveIdx.value)
  }
}, {deep: true})

const isVariantLoaded = (variant: any) => {
  return Array.isArray(variant) && (variant.length === 0 || (variant[0]?.values && variant[0].values.length > 0))
}

watch(activeGroove, () => {
  if (
      isVariantLoaded(activeGroove.value.groove_type) &&
      isVariantLoaded(activeGroove.value.width) &&
      isVariantLoaded(activeGroove.value.object) &&
      isVariantLoaded(activeGroove.value.object_arrangement) &&
      isVariantLoaded(activeGroove.value.amplitude)
  ) {
    loading.value = false
  } else {
    loading.value = true
  }
}, {deep: true})

/**
 * groove details - collapsible sections (open/closeable nodes)
 */

const openSections = reactive<Record<string, boolean>>({
  amplitude: false,
  groove_type: false,
  width: false,
  object: false,
})

const toggleSection = (key: string) => {
  openSections[key] = !openSections[key]
}

/**
 * groove switch (like disc select button)
 */

const grooveList = computed(() => Object.values(grooves.value))

const grooveTabIdx = computed(() => {
  if (activeGrooveIdx.value === -1) return 0
  const idx = grooveList.value.findIndex((groove: any) => groove.groove_idx === activeGrooveIdx.value)
  return idx === -1 ? 0 : idx + 1
})

const grooveSliderStyle = computed(() => {
  const count = grooveList.value.length + 1
  const width = 100 / count
  const left = grooveTabIdx.value * width

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  }
})

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  value = (typeof value == "string") ? value : value.idx
  emit('patch', {
    action: actionIdx,
    value: value,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * groove details - width
 */

const widthValues = computed(() => activeGroove.value.width[0]?.values || [])

const widthIdx = computed(() => {
  const idx = widthValues.value.findIndex((v: any) => v.match)
  return idx === -1 ? 0 : idx
})

// local slider position, so dragging the handle only updates the UI - the api
// call is only sent once the user drops (releases) the slider, see onWidthRangeChange
const sliderWidthIdx = ref(0)

watch(widthIdx, (idx) => {
  sliderWidthIdx.value = idx
}, {immediate: true})

const widthSliderPercent = computed(() => {
  if (widthValues.value.length < 2) return 0
  return (sliderWidthIdx.value / (widthValues.value.length - 1)) * 100
})

const currentWidth = computed(() => widthValues.value[sliderWidthIdx.value])

const stepWidth = (delta: number) => {
  if (!activeGroove.value.width[0]) return
  const newIdx = widthIdx.value + delta
  if (newIdx < 0 || newIdx >= widthValues.value.length) return
  handleValuePatch(activeGroove.value.width[0].idx, widthValues.value[newIdx])
}

const onWidthRangeInput = (event: Event) => {
  sliderWidthIdx.value = parseInt((event.target as HTMLInputElement).value)
}

const onWidthRangeChange = (event: Event) => {
  if (!activeGroove.value.width[0]) return
  const idx = parseInt((event.target as HTMLInputElement).value)
  const value = widthValues.value[idx]
  if (value) handleValuePatch(activeGroove.value.width[0].idx, value)
}

const parseWidthTitle = (title: string) => {
  if (!title) return NaN
  const match = title.replace(',', '.').match(/[\d.]+/)
  return match ? parseFloat(match[0]) : NaN
}

const widthStepLabel = computed(() => {
  if (widthValues.value.length < 2) return ''
  const first = parseWidthTitle(widthValues.value[0]?.title)
  const second = parseWidthTitle(widthValues.value[1]?.title)
  if (isNaN(first) || isNaN(second)) return ''
  const step = Math.abs(second - first)
  return step.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',')
})

/**
 * groove details - object (Bezug)
 */

const showObjectDropdown = ref(false)

const currentObject = computed(() => (activeGroove.value.object[0]?.values || []).find((v: any) => v.match))

/**
 * groove details - object arrangement (Ausrichtung) - step slider
 *
 * Rendered as a step slider (like the width slider above): the title of the currently
 * selected position is shown above the track, and every entry in
 * activeGroove.value.object_arrangement[0].values is one step. Some steps may be
 * marked as not allowed/sendable by the API - those stay visible on the track but
 * can't be dragged onto/selected (see isArrangementDisabled).
 */

const arrangementValues = computed(() => activeGroove.value.object_arrangement[0]?.values || [])

const isArrangementDisabled = (value: any) => {
  return value?.disabled === true || value?.sendable === false || value?.selectable === false || value?.available === false
}

const arrangementIdx = computed(() => {
  const idx = arrangementValues.value.findIndex((v: any) => v.match)
  return idx === -1 ? 0 : idx
})

// local slider position, so dragging the handle only updates the UI - the api call is
// only sent once the user drops (releases) the slider, see onArrangementRangeChange
const sliderArrangementIdx = ref(0)

watch(arrangementIdx, (idx) => {
  sliderArrangementIdx.value = idx
}, {immediate: true})

const arrangementStepPercent = (idx: number) => {
  if (arrangementValues.value.length < 2) return 0
  return (idx / (arrangementValues.value.length - 1)) * 100
}

const arrangementSliderPercent = computed(() => arrangementStepPercent(sliderArrangementIdx.value))

const currentArrangement = computed(() => arrangementValues.value[sliderArrangementIdx.value])

// The image bucket only keeps one folder per object shape ("ring" / "disc"), not one per
// disc idx (e.g. "disc-1") - so a "disc-<id>" object idx is normalized down to "disc".
const arrangementImageObject = computed(() => {
  console.log(currentObject.value)
  const idx = currentObject.value?.idx?.toString() || ''
  return idx.toLowerCase().startsWith('disc') ? 'disc' : idx
})

const arrangementImageUrl = computed(() => {
  if (!arrangementImageObject.value || !currentArrangement.value?.idx) return ''
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/object_arrangement/"
      + arrangementImageObject.value + "/256/" + currentArrangement.value.idx + ".webp"
})

const onArrangementRangeInput = (event: Event) => {
  sliderArrangementIdx.value = parseInt((event.target as HTMLInputElement).value)
}

const onArrangementRangeChange = (event: Event) => {
  if (!activeGroove.value.object_arrangement[0]) return
  const idx = parseInt((event.target as HTMLInputElement).value)
  const value = arrangementValues.value[idx]
  if (value && !isArrangementDisabled(value)) {
    handleValuePatch(activeGroove.value.object_arrangement[0].idx, value)
  } else {
    // dropped on a step that isn't allowed/sendable - snap back to the current selection
    sliderArrangementIdx.value = arrangementIdx.value
  }
}

/**
 * groove details - groove type grouping (transferred from itemV2GrooveType.vue's
 * "activeGroove.grooveTypeVariant" handling)
 *
 * groove_type values are grouped by value.conf_groups.idx: only the value with
 * conf_groups.group_head == true represents its group in the main "Fugenform" list.
 * Every value belonging to the currently selected group is shown again in the
 * "Verlauf" list - both lists patch the exact same variant idx.
 */

const grooveTypeValues = computed(() => activeGroove.value.groove_type[0]?.values || [])

const groupedGrooveTypeValues = computed(() => {
  const groups: Record<string, any[]> = {}
  grooveTypeValues.value.forEach((value: any, idx: number) => {
    const groupIdx = value.conf_groups?.idx ?? value.idx
    if (!groups[groupIdx]) groups[groupIdx] = []
    value.list_idx = idx
    groups[groupIdx].push(value)
  })
  return groups
})

// order of the groove type variants inside the "Fugenform" list - values whose idx
// doesn't match any of these keywords are sorted last (treated as "")
const grooveTypeOrderKeywords = ["u-groove", "v-groove", "bead-groove", "matt-groove", ""]

const getGrooveTypeOrderPriority = (value: any) => {
  const idx = value?.idx?.toString() ?? ""
  const priority = grooveTypeOrderKeywords.findIndex((keyword) => keyword !== "" && idx.includes(keyword))
  return priority === -1 ? grooveTypeOrderKeywords.length - 1 : priority
}

const grooveTypeGroupHeadValues = computed(() => {
  const headValues = Object.values(groupedGrooveTypeValues.value)
      .map((groupValues: any) => groupValues.find((v: any) => v.conf_groups?.group_head) || groupValues[0])
      .filter(Boolean)
  return [...headValues].sort((a, b) => getGrooveTypeOrderPriority(a) - getGrooveTypeOrderPriority(b))
})

const selectedGrooveTypeGroupIdx = computed<string | null>(() => {
  const matched = grooveTypeValues.value.find((v: any) => v.match)
  if (!matched) return null
  return matched.conf_groups?.idx ?? matched.idx ?? null
})

const selectedGrooveTypeGroupValues = computed(() => {
  if (selectedGrooveTypeGroupIdx.value === null) return []
  return groupedGrooveTypeValues.value[selectedGrooveTypeGroupIdx.value] || []
})

/**
 * edge
 */

const getEdgeGrooveHead = computed(() => {
  // return the active groove if match = true and conf_groups.idx = edge
  // if match = true groove is not conf_groups.idx = edge get the groove with the conf_groups.group_head = true and conf_groups.idx = edge
  const matched = grooveTypeValues.value.find((v: any) => v.match)
  if (!matched) return null
  if (matched.conf_groups?.idx === "edge") return matched
  return grooveTypeValues.value.find((v: any) => v.conf_groups?.group_head && v.conf_groups?.idx === "edge") ?? null
})

const isActiveGrooveEdge = computed(() => {
  return getEdgeGrooveHead.value?.match
})

const alternativeGrooveTypeGroupValues = computed(() => {
  if (selectedGrooveTypeGroupIdx.value === null) return []
  for (let grooveGroupIdx in groupedGrooveTypeValues.value) {
    if (grooveGroupIdx !== "edge") return groupedGrooveTypeValues.value[grooveGroupIdx]
  }
  return groupedGrooveTypeValues.value[selectedGrooveTypeGroupIdx.value] || []
})

const grooveVariantsList = computed(() => {
  if (isActiveGrooveEdge.value) {
    return alternativeGrooveTypeGroupValues.value
  } else {
    return selectedGrooveTypeGroupValues.value
  }
})


/**
 * images
 */

const getGrooveCloseup = (groove_type: string) => {
  const key = groove_type.toLowerCase()
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/groove_type/600x600/" + key + ".webp"
}

const getGrooveForm = (groove_type: string) => {
  const key = groove_type.toLowerCase()
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/groove_form/800x600/" + key + ".webp"
}

const getGrooveVariant = (groove_type: string) => {
  const key = groove_type.toLowerCase()
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/groove_variant/800x600/" + key + ".webp"
}

const getAmplitudeCloseup = (amplitude: number) => {
  const key = Number(amplitude).toFixed(1).replace(".", "")
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/amplitude/800x600/groove_amplitude_" + key + ".webp"
}

/**
 * remove groove
 */

const showRemoveModal = ref(false)
const grooveToRemove = ref<any>(null)

const confirmRemoveGroove = (groove: any) => {
  grooveToRemove.value = groove
  showRemoveModal.value = true
};

const grooveToRemoveName = computed(() => {
  return grooveToRemove.value ? `Fuge ${grooveToRemove.value.groove_idx + 1}` : ''
})

const handleRemoveGroove = () => {
  if (grooveToRemove.value) {
    handleValuePatch(`grooves.${grooveToRemove.value.groove_idx}.remove`, 'remove')
  }
  showRemoveModal.value = false
  grooveToRemove.value = null
};

/**
 * tip texts
 */

const getColorTipTitle = computed(() => {

  let key = "common"
  if (activeGroove.value.groove_type && activeGroove.value.groove_type.length > 0 && hoverGrooveTypeIdx.value != -1) {
    key = activeGroove.value.groove_type[0].values[hoverGrooveTypeIdx.value].idx
  }
  return t('components.parameters.tips.grooves.' + key + '.title')
})

const getColorTipText = computed(() => {
  let key = "common"
  if (activeGroove.value.groove_type && activeGroove.value.groove_type.length > 0 && hoverGrooveTypeIdx.value != -1) {
    key = activeGroove.value.groove_type[0].values[hoverGrooveTypeIdx.value].idx
  }
  return t('components.parameters.tips.grooves.' + key + '.text')
})

const getColorTipImageUrl = computed(() => {
  if (activeGroove.value.groove_type && activeGroove.value.groove_type.length > 0 && hoverGrooveTypeIdx.value != -1) {
    return getGrooveCloseup(activeGroove.value.groove_type[0].values[hoverGrooveTypeIdx.value].idx)
  }
  return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/grooves/300x300/common.webp"
})


</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Fugen"
      :subtitle="currentGroovesText || 'Mit oder ohne Fugen - und in welcher Ausführung?'"
      :loading=loading
      :tip-title="getColorTipTitle"
      :tip-text="getColorTipText"
      :tip-image="getColorTipImageUrl"
  >
    <template v-slot:main>

      <div v-if="activeGrooveIdx != -1" class="w-full">
        <!-- groove details -->
        <nav class="w-full mb-4">
          <!-- groove list switch between grooves -->
          <div
              class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
            <!-- Sliding Background -->
            <div
                class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                :style="grooveSliderStyle"
            ></div>

            <button
                type="button"
                class="text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="activeGrooveIdx === -1 ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="activeGrooveIdx = -1"
            >
              Übersicht
            </button>
            <button
                v-for="(groove, grooveKey) in grooves"
                :key="grooveKey"
                type="button"
                class="text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="activeGrooveIdx === groove.groove_idx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="activeGrooveIdx = groove.groove_idx"
            >
              Fuge {{ groove.groove_idx + 1 }}
            </button>
          </div>
        </nav>
        <div v-if="selectedGrooveTypeGroupValues.length">
          <!-- groove type variants -->
          <ul class="flex flex-wrap gap-2 w-full justify-center ">
            <li v-for="(grooveVariant, grooveVariantIdx) in grooveVariantsList" :key="grooveVariantIdx"
                class="cursor-pointer flex flex-col items-center gap-1.5 w-15"
                @click="handleValuePatch(activeGroove.groove_type[0].idx, grooveVariant)"
                @mouseover="hoverGrooveTypeIdx=grooveVariant.list_idx"
                @mouseout="hoverGrooveTypeIdx=-1"
            >
                  <span
                      class="flex aspect-retro shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc]"
                      :class="grooveVariant.match ? 'border-brand-main' : 'border-[#e7ddcc] hover:border-brand-main/50'"
                  >
                    <img :src="getGrooveVariant(grooveVariant.idx)" alt=""
                         class="size-full aspect-retro object-cover"/>
                  </span>
              <span class="text-center text-[10px] leading-tight text-parameter-text">{{
                  grooveVariant.text_variant
                }}</span>
            </li>
            <li v-if="getEdgeGrooveHead" class="cursor-pointer flex flex-col items-center gap-1.5 w-15"
                @click="handleValuePatch(activeGroove.groove_type[0].idx, getEdgeGrooveHead.idx)"
                @mouseover="hoverGrooveTypeIdx=getEdgeGrooveHead.list_idx"
                @mouseout="hoverGrooveTypeIdx=-1"
            >
                <span
                    class="flex aspect-retro shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc]"
                    :class="getEdgeGrooveHead.match ? 'border-brand-main' : 'border-[#e7ddcc] hover:border-brand-main/50'"
                >
                  <img :src="getGrooveVariant(getEdgeGrooveHead.idx)" alt=""
                       class="size-full aspect-retro object-cover"/>
                </span>
              <span class="text-center text-[10px] leading-tight text-parameter-text">{{
                  getEdgeGrooveHead.text_form
                }}</span>
            </li>
          </ul>
        </div>
      </div>
      <template v-else>
        <div v-if="grooves" class="w-full">
          <button
              type="button"
              class="flex w-full items-center gap-3 rounded-2xl p-2 text-left transition-colors hover:bg-[#f3f0e9] bg-white border border-gray-200 cursor-pointer"
              :class="!hasGrooves ? 'border-brand-main bg-[#f3f0e9]' : ''"
              @click="handleValuePatch(`grooves.remove`, 'remove')"
          >
          <span
              class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border"
              :class="!hasGrooves ? 'border-brand-main' : 'border-[#e7ddcc]'"
          >
            <img
                src="https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/groove_type/600x600/empty.webp"
                alt=""/>

          </span>
            <span class="text-sm font-medium tracking-[0.1px] text-parameter-title">Ohne Fugen</span>
          </button>
        </div>
        <div class="mt-4 w-full">
          <h6 v-if="hasGrooves" class="m-0 text-[15px] leading-5 font-normal text-parameter-title">Ihre Fugen</h6>
          <ul class="mt-1">
            <li v-for="(groove, grooveIdx) in grooves" :key="'groove-' + grooveIdx"
                class="flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-[#f3f0e9] bg-white border border-gray-200 cursor-pointer"
            >
            <span
                class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc]"
                @click="activeGrooveIdx = groove.groove_idx"

            >
              <img :src="getGrooveCloseup(groove.key.groove_type)" alt=""/>
            </span>
              <div class="min-w-0 flex-1"
                   @click="activeGrooveIdx = groove.groove_idx"
              >
              <span class="block text-sm font-medium tracking-[0.1px] text-parameter-title">Fuge {{
                  groove.groove_idx + 1
                }}</span>
                <span class="block truncate text-xs leading-[15px] text-parameter-text">
                {{ groove.groove_type }} · {{ groove.width }}
              </span>
                <span class="block truncate text-xs leading-[15px] text-parameter-text">
                {{ groove.object }} · {{ groove.object_arrangement }}
              </span>
              </div>
              <button
                  type="button"
                  class="flex size-8 shrink-0 items-center justify-center rounded-full text-[#8f8a80] transition-colors hover:bg-white hover:text-brand-main"
                  title="Bearbeiten"
                  @click="activeGrooveIdx = groove.groove_idx"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"></path>
                </svg>
              </button>
              <button
                  type="button"
                  class="flex size-8 shrink-0 items-center justify-center rounded-full text-[#8f8a80] transition-colors hover:bg-white hover:text-red-500"
                  title="Entfernen"
                  @click="confirmRemoveGroove(groove)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </li>
          </ul>
          <button
              type="button"
              class="mt-2 flex w-full items-center justify-center gap-2 rounded-[11px] border border-dashed border-parameter-text py-3 text-xs font-medium tracking-[0.1px] text-parameter-text transition-colors hover:bg-[#f3f0e9] hover:text-brand-main cursor-pointer"
              @click="handleValuePatch('grooves.0.add', 'add')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Fuge hinzufügen
          </button>
        </div>
      </template>

    </template>
    <template v-slot:details>
      <div v-if="activeGrooveIdx != -1">
        <!-- groove details -->
        <div>
          <div class="mb-4" v-if="activeGroove.amplitude[0]">
            <!-- groove amplitude -->
            <button
                type="button"
                class="flex w-full items-center gap-3 mb-2 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
                :aria-expanded="openSections.amplitude"
                @click="toggleSection('amplitude')"
            >
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
              <span
                  class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
                Wellenhöhe
                <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                     class="size-2.5 shrink-0 text-[#555956] transition-transform"
                     :class="openSections.amplitude ? 'rotate-180' : ''">
                  <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
            </button>
            <ul v-if="openSections.amplitude" class="flex gap-2 flex-wrap w-full justify-center ">
              <li
                  v-for="(grooveAmplitude, grooveAmplitudeIdx) in activeGroove.amplitude[0].values"
                  :key="grooveAmplitudeIdx"
                  @click="handleValuePatch(activeGroove.amplitude[0].idx, grooveAmplitude)"
                  class="cursor-pointer flex flex-col items-center gap-1.5 w-15"
              >
                <span
                    class="flex w-full h-auto shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-[#e7ddcc] cursor-pointer"
                    :class="grooveAmplitude.match ? 'border-brand-main' : 'border-[#e7ddcc] hover:border-brand-main/50'"
                >
                  <img :src="getAmplitudeCloseup(grooveAmplitude.idx)" alt=""
                       class="size-full object-cover"/>
                </span>
                <span class="text-center text-[10px] leading-tight text-parameter-text w-full mx-auto">{{
                    grooveAmplitude.title
                  }}</span>

              </li>
            </ul>
          </div>
          <div class="mb-4" v-if="activeGroove.groove_type[0]">
            <!-- groove type -->
            <button
                type="button"
                class="flex w-full items-center gap-3 mb-2 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
                :aria-expanded="openSections.groove_type"
                @click="toggleSection('groove_type')"
            >
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
              <span
                  class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
                Fugenform
                <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                     class="size-2.5 shrink-0 text-[#555956] transition-transform"
                     :class="openSections.groove_type ? 'rotate-180' : ''">
                  <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
            </button>

            <ul v-if="openSections.groove_type" class="flex flex-wrap gap-2  w-full justify-center ">
              <template v-if="isActiveGrooveEdge">
                <li v-for="(grooveVariant, grooveVariantIdx) in selectedGrooveTypeGroupValues" :key="grooveVariantIdx"
                    class="cursor-pointer flex flex-col items-center gap-1.5 w-15"
                    @click="handleValuePatch(activeGroove.groove_type[0].idx, grooveVariant)"
                    @mouseover="hoverGrooveTypeIdx=grooveVariant.list_idx"
                    @mouseout="hoverGrooveTypeIdx=-1"
                >
                  <span
                      class="flex aspect-retro shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc]"
                      :class="grooveVariant.match ? 'border-brand-main' : 'border-[#e7ddcc] hover:border-brand-main/50'"
                  >
                    <img :src="getGrooveVariant(grooveVariant.idx)" alt=""
                         class="size-full aspect-retro object-cover"/>
                  </span>
                  <span class="text-center text-[10px] leading-tight text-parameter-text">aa{{
                      grooveVariant.text_variant
                    }}</span>
                </li>
              </template>
              <template v-else>
                <template v-for="(grooveType, grooveTypeIdx) in grooveTypeGroupHeadValues">
                  <li v-if="grooveType.conf_groups.idx != 'edge'" :key="grooveTypeIdx"
                      class="cursor-pointer flex flex-col items-center gap-1.5 w-16"
                      @click="handleValuePatch(activeGroove.groove_type[0].idx, grooveType)"
                      @mouseover="hoverGrooveTypeIdx=grooveType.list_idx"
                      @mouseout="hoverGrooveTypeIdx=-1"
                  >
                <span
                    class="flex aspect-retro shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc]"
                    :class="grooveType.match ? 'border-brand-main' : 'border-[#e7ddcc] hover:border-brand-main/50'"
                >
                  <img :src="getGrooveForm(grooveType.idx)" alt=""
                       class="size-full object-cover aspect-retro"/>
                </span>
                    <span class="text-center text-[10px] leading-tight text-parameter-text">{{
                        grooveType.text_form
                      }}</span>
                  </li>
                </template>
              </template>
            </ul>
          </div>
          <div class="mb-4" v-if="activeGroove.width[0]">
            <!-- groove width and postion -->
            <button
                type="button"
                class="flex w-full items-center gap-3 mb-2 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
                :aria-expanded="openSections.width"
                @click="toggleSection('width')"
            >
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
              <span
                  class="flex items-center gap-1.5 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
                Breite / Position
                <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                     class="size-2.5 shrink-0 text-[#555956] transition-transform"
                     :class="openSections.width ? 'rotate-180' : ''">
                  <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="h-px flex-1 bg-[#e7ddcc]"></span>
            </button>
            <div v-if="openSections.width" class="flex divide-x divide-[#e7ddcc]">
              <!-- groove width -->
              <div class="w-1/2 px-4 first:pl-0 last:pr-0">
                <h6 class="m-0 mb-2 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
                  Breite</h6>
                <div class="flex items-center gap-2">
                  <button
                      type="button"
                      class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
                      :disabled="widthIdx <= 0"
                      @click="stepWidth(-1)"
                  >
                    −
                  </button>
                  <span
                      class="flex-1 rounded-xl border border-[#e7ddcc] py-1.5 text-center text-xs font-medium text-parameter-title">
                {{ currentWidth?.title }}
              </span>
                  <button
                      type="button"
                      class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
                      :disabled="widthIdx >= widthValues.length - 1"
                      @click="stepWidth(1)"
                  >
                    +
                  </button>
                </div>
                <div class="relative mt-4 flex h-4 items-center select-none">
                  <!-- track -->
                  <div class="pointer-events-none absolute inset-x-0 h-[3px] rounded-full bg-[#e7ddcc]">
                    <div class="h-full rounded-full bg-brand-main" :style="{ width: widthSliderPercent + '%' }"></div>
                  </div>
                  <!-- thumb -->
                  <div
                      class="pointer-events-none absolute top-1/2 size-4 rounded-full bg-brand-main shadow-md"
                      :style="{ left: widthSliderPercent + '%', transform: 'translate(-50%, -50%)' }"
                  ></div>
                  <!-- native range input, kept invisible but interactive -->
                  <input
                      type="range"
                      class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                      min="0"
                      :max="widthValues.length - 1"
                      step="1"
                      :value="sliderWidthIdx"
                      @input="onWidthRangeInput"
                      @change="onWidthRangeChange"
                  />
                </div>
                <div class="mt-1 flex justify-between text-[10px] text-parameter-text">
                  <span>{{ widthValues[0]?.title }}</span>
                  <span>{{ widthValues[widthValues.length - 1]?.title }}</span>
                </div>
              </div>
              <!-- groove object + object arrangement, combined as "Position" -->
              <div v-if="activeGroove.object[0] || activeGroove.object_arrangement[0]"
                   class="w-1/2 px-4 first:pl-0 last:pr-0">
                <h6 class="m-0 mb-2 text-xs leading-[15px] font-normal tracking-[0.1px] text-parameter-text whitespace-nowrap">
                  Position</h6>
                <!-- groove object -->
                <div v-if="activeGroove.object[0]" class="overflow-hidden rounded-2xl border border-[#e7ddcc] bg-white">
                  <button
                      type="button"
                      class="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-xs text-parameter-title transition-colors hover:bg-[#f3f0e9]"
                      @click="showObjectDropdown = !showObjectDropdown"
                  >
                    <span>{{ currentArrangement?.title }} {{ currentObject?.title }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 shrink-0 transition-transform"
                         :class="showObjectDropdown ? 'rotate-180' : ''"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                         stroke-linejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  <ul v-if="showObjectDropdown" class="border-t border-[#e7ddcc] py-1">
                    <li v-for="(grooveObject, grooveObjectIdx) in activeGroove.object[0].values" :key="grooveObjectIdx"
                        class="mx-1.5 cursor-pointer rounded-xl px-2.5 py-2 text-xs transition-colors hover:bg-[#f3f0e9]"
                        :class="grooveObject.match ? 'bg-[#e7ddcc] font-medium text-parameter-title' : 'text-parameter-text'"
                        @click="handleValuePatch(activeGroove.object[0].idx, grooveObject); showObjectDropdown = false"
                    >
                      {{ currentArrangement?.title }} {{ grooveObject.title }}
                    </li>
                  </ul>
                </div>
                <!-- object arrangement -->
                <div v-if="activeGroove.object_arrangement[0]" class="mt-4">
                  <p v-if="false" class="m-0 text-center text-xs font-medium text-parameter-title">
                    {{ currentArrangement?.title }}</p>
                  <div class="relative mt-4 flex h-4 items-center select-none">
                    <!-- track -->
                    <div class="pointer-events-none absolute inset-x-0 h-[3px] rounded-full bg-[#e7ddcc]">
                      <div class="h-full rounded-full bg-brand-main"
                           :style="{ width: arrangementSliderPercent + '%' }"></div>
                    </div>
                    <!-- step markers - steps that aren't allowed/sendable stay visible but muted -->
                    <span
                        v-for="(grooveObjectArrangement, grooveObjectArrangementIdx) in arrangementValues"
                        :key="grooveObjectArrangementIdx"
                        :title="grooveObjectArrangement.title"
                        class="pointer-events-none absolute top-1/2 size-1.5 rounded-full"
                        :class="isArrangementDisabled(grooveObjectArrangement) ? 'bg-[#c9c2b3]' : 'border border-brand-main bg-white'"
                        :style="{ left: arrangementStepPercent(grooveObjectArrangementIdx) + '%', transform: 'translate(-50%, -50%)' }"
                    ></span>
                    <!-- thumb -->
                    <div
                        class="pointer-events-none absolute top-1/2 size-4 rounded-full bg-brand-main shadow-md"
                        :style="{ left: arrangementSliderPercent + '%', transform: 'translate(-50%, -50%)' }"
                    ></div>
                    <!-- native range input, kept invisible but interactive -->
                    <input
                        type="range"
                        class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                        min="0"
                        :max="arrangementValues.length - 1"
                        step="1"
                        :value="sliderArrangementIdx"
                        @input="onArrangementRangeInput"
                        @change="onArrangementRangeChange"
                    />
                  </div>
                  <div class="w-full mt-2 h-20 overflow-hidden">
                    <img
                        v-if="arrangementImageUrl"
                        :src="arrangementImageUrl"
                        :alt="currentArrangement?.title || ''"
                        class="aspect-square w-full max-w-44 -rotate-90 object-contain relative -top-1/2 mx-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          </div>
        </div>
      </div>
    </template>
  </item-v3-parameter-box>

  <!-- Remove Groove Confirmation Modal -->
  <item-v3-remove-group-modal v-model="showRemoveModal" :item-name="grooveToRemoveName" @confirm="handleRemoveGroove"/>
</template>